import L from 'leaflet';

/**
 * Leaflet resolves its default marker sprite relative to the stylesheet, which
 * a bundler breaks. The images are required from the installed package so the
 * version can never diverge from the runtime: TripDetailPage used to pull them
 * from a CDN pinned to Leaflet 1.7.1 while 1.9.4 was bundled, which also made
 * a trip page unable to render its own map offline.
 */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * Rebuilding a divIcon makes Leaflet replace the marker's DOM node. Fleet
 * updates arrive every few seconds for every truck, so an unmemoised factory
 * re-created the whole marker layer on each message and the map stuttered.
 * Icons are stateless — Leaflet calls `createIcon()` per marker — so one
 * instance can back every truck that shares a colour, plate and online state.
 */
const ICON_CACHE = new Map();
const ICON_CACHE_MAX = 512;

/** Un texte glissé dans du HTML ne doit jamais pouvoir ouvrir une balise. */
export const echapperHtml = (valeur) =>
  String(valeur ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** Le cap est arrondi par pas de 15° : assez fin à l'œil, et le cache reste petit. */
const PAS_CAP = 15;
export const capArrondi = (cap) => {
  const n = Number(cap);
  if (cap === null || cap === undefined || cap === '' || !Number.isFinite(n)) return null;
  return (((Math.round(n / PAS_CAP) * PAS_CAP) % 360) + 360) % 360;
};

// Camion dessiné en trait (même tracé que l'icône « Truck » du reste de l'application).
const PICTO_CAMION =
  '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';

/**
 * Marqueur d'un camion.
 *  - `heading` (degrés) : une pointe tournée autour du disque, seulement quand
 *    le camion bouge (`moving`) ; un camion arrêté n'a pas de direction ;
 *  - la plaque est toujours dans le marqueur, mais la feuille de style la masque
 *    quand la carte est dézoomée (classe `fleet-plates-hidden` posée par
 *    TruckLayer), sauf au survol, au focus et pour le camion sélectionné : sinon
 *    les plaques se chevauchent. Le changement de zoom ne refait donc aucune icône ;
 *  - `description` : nom accessible et infobulle (« AA-123 — En route, … »).
 */
export function truckIcon({
  color,
  matricule,
  online,
  heading = null,
  moving = false,
  selected = false,
  description = '',
}) {
  const cap = moving ? capArrondi(heading) : null;
  const key = `${color}|${matricule || ''}|${online ? 1 : 0}|${cap ?? ''}|${selected ? 1 : 0}|${description}`;
  const cached = ICON_CACHE.get(key);
  if (cached) return cached;

  const borderColor = online ? 'hsl(var(--success))' : 'hsl(var(--destructive))';
  const pointe =
    cap === null
      ? ''
      : `<div style="position:absolute; left:0; top:0; width:44px; height:44px; transform: rotate(${cap}deg); pointer-events:none;">
           <div style="position:absolute; left:15px; top:-1px; width:0; height:0;
             border-left:7px solid transparent; border-right:7px solid transparent;
             border-bottom:9px solid ${color || 'hsl(var(--flame))'};
             filter: drop-shadow(0 0 1px hsl(var(--card)));"></div>
         </div>`;
  // The plate label used to be hard-coded white-on-#111: the one place in the
  // map that ignored the token system, and unreadable on the dark theme.
  // divIcon markup lives inside the document, so the custom properties cascade.
  const plaque = `<div class="fleet-truck-plate" style="
        position:absolute; left:50%; top:46px; transform: translateX(-50%);
        background: hsl(var(--card) / 0.95); color: hsl(var(--card-foreground));
        padding: 1px 6px; border-radius: 4px; font-size: 11px;
        font-weight: 600; white-space: nowrap;
        box-shadow: 0 1px 3px hsl(var(--foreground) / 0.25);
        border: 1px solid hsl(var(--border));
      ">${echapperHtml(matricule || '—')}</div>`;
  const html = `
    <div class="${selected ? 'fleet-truck-selected' : ''}" title="${echapperHtml(description)}" style="position: relative; width: 44px; height: 44px;">
      ${pointe}
      <div style="
        position:absolute; left:6px; top:6px; width: 32px; height: 32px; border-radius: 50%;
        box-sizing: border-box;
        background: ${color || 'hsl(var(--flame))'};
        border: 3px solid ${borderColor};
        box-shadow: 0 2px 6px hsl(var(--foreground) / 0.35)${selected ? ', 0 0 0 3px hsl(var(--ring))' : ''};
        display:flex; align-items:center; justify-content:center;
        color: hsl(var(--flame-foreground));
      ">${PICTO_CAMION}</div>
      ${plaque}
    </div>
  `;

  const icon = L.divIcon({
    html,
    className: 'fleet-truck-marker',
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -20],
  });

  if (ICON_CACHE.size >= ICON_CACHE_MAX) ICON_CACHE.clear();
  ICON_CACHE.set(key, icon);
  return icon;
}

/** Pastille d'un groupe de camions : le nombre, et rien d'autre. */
export function clusterIcon({ count, description = '' }) {
  const key = `cluster|${count}|${description}`;
  const cached = ICON_CACHE.get(key);
  if (cached) return cached;
  const taille = count >= 100 ? 52 : count >= 10 ? 46 : 40;
  const icon = L.divIcon({
    className: 'fleet-cluster-marker',
    html: `<div title="${echapperHtml(description)}" style="
        width:${taille}px; height:${taille}px; border-radius:50%;
        box-sizing:border-box;
        display:flex; align-items:center; justify-content:center;
        background: hsl(var(--primary)); color: hsl(var(--primary-foreground));
        border: 3px solid hsl(var(--card));
        box-shadow: 0 2px 8px hsl(var(--foreground) / 0.4);
        font-size: 14px; font-weight: 700;
      ">${count}</div>`,
    iconSize: [taille, taille],
    iconAnchor: [taille / 2, taille / 2],
  });
  if (ICON_CACHE.size >= ICON_CACHE_MAX) ICON_CACHE.clear();
  ICON_CACHE.set(key, icon);
  return icon;
}

/** Small dot used for route endpoints and the zone picker handle. */
export function dotIcon({ color = 'hsl(var(--primary))', size = 14, label } = {}) {
  const key = `dot|${color}|${size}|${label || ''}`;
  const cached = ICON_CACHE.get(key);
  if (cached) return cached;

  const icon = L.divIcon({
    className: 'fleet-dot-marker',
    html: `<div style="
        width:${size}px; height:${size}px; border-radius:50%;
        background:${color};
        border:2px solid hsl(var(--card));
        box-shadow:0 1px 4px hsl(var(--foreground) / 0.35);
      " ${label ? `title="${label}"` : ''}></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

  ICON_CACHE.set(key, icon);
  return icon;
}

// Triangle d'alerte (même tracé que l'icône « TriangleAlert » du reste de l'application).
const PICTO_ALERTE =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>';

/** Marqueur d'une alerte posée sur la carte (arrêt critique, hors itinéraire…). */
export function alertIcon({ critique = false, description = '' } = {}) {
  const key = `alerte|${critique ? 1 : 0}|${description}`;
  const cached = ICON_CACHE.get(key);
  if (cached) return cached;
  const fond = critique ? 'hsl(var(--destructive))' : 'hsl(var(--flame))';
  const icon = L.divIcon({
    className: 'fleet-alert-marker',
    html: `<div title="${echapperHtml(description)}" style="width:26px;height:26px;border-radius:9999px;background:${fond};color:#fff;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)">${PICTO_ALERTE}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14],
  });
  if (ICON_CACHE.size >= ICON_CACHE_MAX) ICON_CACHE.delete(ICON_CACHE.keys().next().value);
  ICON_CACHE.set(key, icon);
  return icon;
}

/** Pastille numérotée d'un arrêt, sur la lecture d'un trajet. */
export function arretIcon(numero) {
  const key = `arret|${numero}`;
  const cached = ICON_CACHE.get(key);
  if (cached) return cached;
  const icon = L.divIcon({
    className: 'trajet-arret-marker',
    html: `<div style="
        width:24px; height:24px; border-radius:50%; box-sizing:border-box;
        display:flex; align-items:center; justify-content:center;
        background: hsl(var(--card)); color: hsl(var(--foreground));
        border: 2px solid hsl(var(--muted-foreground));
        box-shadow: 0 1px 4px hsl(var(--foreground) / 0.35);
        font-size: 12px; font-weight: 700; line-height: 1;
      ">${echapperHtml(numero)}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
  if (ICON_CACHE.size >= ICON_CACHE_MAX) ICON_CACHE.clear();
  ICON_CACHE.set(key, icon);
  return icon;
}
